import React, { useRef, useEffect, useState } from "react";
import WindowHeader from "../../../Layout/WindowHeader/WindowHeader";
import ActionToolbar from "../../../NavBars/ActionToolbar";
import SherComboBox from "../../../ComboBoxes/SherComboBox";
import ssLogo from "../../../../assets/images/sslogo.png";
import axios from "axios";
import io from "socket.io-client";
import { useDbContext } from "../../../../context/DbContext";
import { useSelector } from "react-redux";
import { selectMenuPermissions } from "../../../../app/permissionsSlice";
import { selectActiveLocation } from "../../../../app/locationSlice";

const emitModelUpdate = (apiBaseUrl) => {
  if (!apiBaseUrl) return;
  const socket = io(apiBaseUrl);
  socket.emit("ModelUpdated");
  socket.disconnect();
};

function ModelReg({ onClose, zIndex }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();
  const permissions = useSelector(selectMenuPermissions);
  const activeLocation = useSelector(selectActiveLocation);

  const modelNameRef = useRef(null);
  const modelCodeRef = useRef(null);
  const descriptionRef = useRef(null);
  const saveBtnRef = useRef(null);

  const [saveDisabled, setSaveDisabled] = useState(false);
  const [formData, setFormData] = useState({
    modelName: "",
    modelCode: "",
    description: "",
    active: 1,
  });
  const [selectedId, setSelectedId] = useState("");
  const [modelList, setModelList] = useState([]);

  const fetchModels = async () => {
    if (!dbCode) return;
    try {
      const response = await axios.get(
        `${apiBaseUrl}/model/selectall/${dbCode}`,
      );
      setModelList(response.data || []);
    } catch (error) {
      console.error("Error fetching models:", error.message);
    }
  };

  useEffect(() => {
    fetchModels();

    const socket = io(apiBaseUrl);
    socket.on("ModelUpdated", fetchModels);
    return () => {
      socket.off("ModelUpdated", fetchModels);
      socket.disconnect();
    };
  }, [apiBaseUrl, dbCode, activeLocation]);

  useEffect(() => {
    setTimeout(() => {
      modelNameRef.current?.focus?.();
    }, 150);
  }, []);

  const handleClear = () => {
    setFormData({ modelName: "", modelCode: "", description: "", active: 1 });
    setSelectedId("");
    modelNameRef.current?.clearInput?.();
    setTimeout(() => modelNameRef.current?.focus?.(), 100);
  };

  const handleSelectRecord = (record) => {
    setSelectedId(String(record.Auto));
    setFormData({
      modelName: record.ModelName || "",
      modelCode: record.ModelCode || "",
      description: record.Description || "",
      active: record.Active !== undefined ? record.Active : 1,
    });
  };

  const handleSubmit = async () => {
    if (!formData.modelName.trim()) {
      alert("Please enter a Model Name");
      modelNameRef.current?.focus?.();
      return;
    }

    const isDuplicate = modelList.some(
      (m) =>
        m.ModelName.toLowerCase() === formData.modelName.toLowerCase().trim(),
    );
    if (isDuplicate) {
      alert("Duplicate entry. This model name already exists.");
      return;
    }

    if (!window.confirm(`Save model "${formData.modelName.trim()}"?`)) return;

    setSaveDisabled(true);
    try {
      const response = await axios.post(
        `${apiBaseUrl}/model/insert/${dbCode}`,
        { ...formData, Location: activeLocation ? activeLocation.id : 0 },
      );
      if (response.status === 200) {
        alert("Model Saved Successfully!");
        emitModelUpdate(apiBaseUrl);
        handleClear();
        fetchModels();
      } else {
        alert("Error saving model.");
      }
    } catch (error) {
      console.error("Error saving model:", error.message);
      alert("Error saving model.");
    } finally {
      setSaveDisabled(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedId) {
      alert("Please select a model to edit");
      return;
    }
    if (!formData.modelName.trim()) {
      alert("Please enter a valid Model Name");
      return;
    }

    if (!window.confirm(`Update model "${formData.modelName.trim()}"?`)) return;

    try {
      const response = await axios.put(`${apiBaseUrl}/model/update/${dbCode}`, {
        auto: selectedId,
        ...formData,
        Location: activeLocation ? activeLocation.id : 0,
      });
      if (response.status === 200) {
        alert("Model Updated Successfully!");
        emitModelUpdate(apiBaseUrl);
        handleClear();
        fetchModels();
      } else {
        alert("Error updating model.");
      }
    } catch (error) {
      console.error("Error updating model:", error.message);
      alert("Error updating model.");
    }
  };

  const handleDelete = async () => {
    if (!selectedId) {
      alert("Please select a model to delete");
      return;
    }
    if (
      !window.confirm(
        `Are you sure you want to delete "${formData.modelName}"?`,
      )
    )
      return;

    try {
      const response = await axios.delete(
        `${apiBaseUrl}/model/delete/${dbCode}/${selectedId}?locationId=${activeLocation ? activeLocation.id : ''}`,
      );
      if (response.status === 200) {
        alert("Model Deleted Successfully!");
        emitModelUpdate(apiBaseUrl);
        handleClear();
        fetchModels();
      } else {
        alert("Error deleting model.");
      }
    } catch (error) {
      console.error("Error deleting model:", error.message);
      alert("Error deleting model.");
    }
  };

  const modelComboOptions = modelList.map((m) => ({
    label: m.ModelName || "",
    value: String(m.Auto || ""),
  }));

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-3 font-Inter select-none"
      style={{ zIndex: zIndex || 101, fontFamily: "Inter, sans-serif" }}
    >
      <div className="bg-white font-normal w-[640px] max-w-[92vw] h-auto rounded-xl shadow-2xl flex flex-col overflow-hidden border border-slate-200">
        {/* Header */}
        <WindowHeader
          title="Model / Design Registration"
          logo={ssLogo}
          onClose={onClose}
          closeBtnClassName="w-6 h-6 border-none rounded-[5px] mr-2"
        />

        {/* Toolbar */}
        <div className="w-full flex-shrink-0">
          <ActionToolbar
            width="100%"
            height="auto"
            bgColor="#EAF0FC"
            size="small"
            iconSize={16}
            showSave={true}
            showPrint={false}
            showClear={true}
            showFind={false}
            showEdit={true}
            showDelete={true}
            showExit={true}
            showNavigation={false}
            saveDisabled={!!selectedId || saveDisabled}
            editDisabled={!selectedId}
            deleteDisabled={!selectedId}
            dataFound={!!selectedId}
            onSave={handleSubmit}
            onClear={handleClear}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onExit={onClose}
          />
        </div>

        {/* Body */}
        <div className="w-full flex-1 p-3.5 bg-slate-50 flex gap-4 items-start">
          {/* LEFT: Form */}
          <div className="flex-1 bg-white p-3.5 rounded-lg border border-slate-200 shadow-sm flex flex-col gap-2.5">
            <h3 className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider m-0">
              ADD / EDIT MODEL
            </h3>

            {/* Model Name */}
            <div className="flex flex-col gap-1">
              <label className="text-[11.5px] font-bold text-slate-700 font-Inter">
                Model / Design Name <span className="text-red-500">*</span>
              </label>
              <SherComboBox
                comboRef={modelNameRef}
                options={modelComboOptions}
                value={formData.modelName}
                onSelect={(opt) => {
                  const found = modelList.find(
                    (m) => String(m.Auto) === String(opt.value),
                  );
                  if (found) handleSelectRecord(found);
                  else
                    setFormData((prev) => ({ ...prev, modelName: opt.label }));
                }}
                onInputChange={(text) =>
                  setFormData((prev) => ({ ...prev, modelName: text }))
                }
                onFreeInput={(text) =>
                  setFormData((prev) => ({ ...prev, modelName: text }))
                }
                placeholder="e.g. Palakka Mala"
                showArrow={true}
                clearable={false}
                inputWidth="100%"
                inputHeight="32px"
                inputClassName="border border-slate-300 rounded-md focus:border-blue-600 shadow-2xs"
                inputStyle={{ fontSize: "12px", paddingLeft: "8px" }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    modelCodeRef.current?.focus?.();
                  }
                }}
              />
            </div>

            {/* Model Code */}
            <div className="flex flex-col gap-1">
              <label className="text-[11.5px] font-bold text-slate-700 font-Inter">
                Model Code
              </label>
              <input
                ref={modelCodeRef}
                type="text"
                value={formData.modelCode}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    modelCode: e.target.value,
                  }))
                }
                placeholder="e.g. PLK-001"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    descriptionRef.current?.focus?.();
                  }
                }}
                className="w-full h-8 px-2.5 text-xs font-medium border border-slate-300 rounded-md outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-500 bg-slate-50/50"
              />
            </div>

            {/* Description */}
            <div className="flex flex-col gap-1">
              <label className="text-[11.5px] font-bold text-slate-700 font-Inter">
                Description
              </label>
              <input
                ref={descriptionRef}
                type="text"
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Short description (optional)"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    selectedId ? handleEdit() : handleSubmit();
                  }
                }}
                className="w-full h-8 px-2.5 text-xs font-medium border border-slate-300 rounded-md outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-500 bg-slate-50/50"
              />
            </div>

            {/* Active toggle */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="modelActive"
                checked={!!formData.active}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    active: e.target.checked ? 1 : 0,
                  }))
                }
                className="w-3.5 h-3.5 accent-blue-600 cursor-pointer"
              />
              <label
                htmlFor="modelActive"
                className="text-[11.5px] font-bold text-slate-700 cursor-pointer"
              >
                Active
              </label>
            </div>

            {/* Save / Update Button */}
            <button
              type="button"
              ref={saveBtnRef}
              onClick={selectedId ? handleEdit : handleSubmit}
              disabled={saveDisabled}
              className="w-full mt-1 py-1.5 px-3 bg-[#1a3abf] hover:bg-[#142e99] active:bg-[#0f2378] text-white font-bold text-xs rounded-lg shadow-sm transition-all cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {selectedId ? "Update Model" : "Save Model"}
            </button>
          </div>

          {/* RIGHT: Existing Models List */}
          <div className="w-[240px] shrink-0 bg-white p-3.5 rounded-lg border border-slate-200 shadow-sm flex flex-col gap-2.5">
            <h3 className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider m-0">
              REGISTERED MODELS
            </h3>

            <div className="flex flex-col gap-1.5 overflow-y-auto max-h-[220px] pr-0.5 report-scroll">
              {modelList.length === 0 ? (
                <div className="py-4 text-center text-xs text-slate-400 italic">
                  No models registered yet
                </div>
              ) : (
                modelList.map((item) => {
                  const isSelected = String(item.Auto) === String(selectedId);
                  return (
                    <div
                      key={item.Auto}
                      onClick={() => handleSelectRecord(item)}
                      className={`flex items-center justify-between p-1.5 px-2.5 rounded-lg border cursor-pointer transition-all duration-200 ${
                        isSelected
                          ? "bg-blue-50 border-blue-400 shadow-2xs"
                          : "bg-slate-50/70 hover:bg-slate-100 border-slate-200"
                      }`}
                    >
                      <div className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-blue-500" />
                        <div className="flex flex-col">
                          <span className="font-bold text-xs text-slate-800 tracking-wide">
                            {item.ModelName}
                          </span>
                          {item.ModelCode && (
                            <span className="text-[9.5px] text-slate-400 font-mono">
                              {item.ModelCode}
                            </span>
                          )}
                        </div>
                      </div>
                      <span
                        className={`text-[9px] font-bold px-1 py-0.5 rounded ${item.Active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}
                      >
                        {item.Active ? "Active" : "Off"}
                      </span>
                    </div>
                  );
                })
              )}
            </div>

            <div className="pt-1.5 border-t border-slate-100 flex items-center gap-1.5 text-[11px] font-semibold text-emerald-600">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span>{modelList.length} models registered</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ModelReg;
